import { z, TypeOf } from 'zod';
import { fieldFilter } from '../FieldFilter';
import { categoryFilter } from '../CategoryFilter';
import {
    productMixAndMatchRequest,
    productMixAndMatchResult
} from '../assistant/ProductMixAndMatch';
import {
    productSearchCombinationsRequest,
    productSearchCombinationsResult
} from '../product/ProductSearchCombinations';
import {
    productSearchSimilarRequest,
    productSearchSimilarResult
} from '../product/ProductSearchSimilar';
import {
    productSearchByTextRequest,
    productSearchByTextResult
} from '../product/ProductSearchByText';
import {
    productSearchComplementaryCategoryRequest,
    productSearchComplementaryCategoryResult
} from '../product/ProductSearchComplementaryCategory';
import {
    productSearchNewRequest,
    productSearchNewResult
} from '../product/ProductSearchNew';
import {
    productSearchSimilarByImageRequest,
    productSearchSimilarByImageResult
} from '../product/ProductSearchSimilarByImage';
import {
    productSearchCombinationsByImageRequest,
    productSearchCombinationsByImageResult
} from '../product/ProductSearchCombinationsByImage';
import {
    productComparisonRequest,
    productComparisonResult
} from '../assistant/ProductComparisonTable';
import { product } from '../Product';

export const baseChatMessage = z.object({
    uuid: z.string().uuid(),
    timestamp: z.number()
});

export const userChatMessageContent = z.object({
    contentType: z.literal('plain-text'),
    text: z.string()
});

export const userChatMessage = baseChatMessage.extend({
    role: z.literal('user'),
    content: userChatMessageContent
});

export const featureError = z.object({
    message: z.string(),
    code: z.enum(['unknown'])
});

export const feature = z.union([
    z.object({
        featureType: z.literal('product-search-by-text'),
        request: productSearchByTextRequest,
        result: productSearchByTextResult.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-search-similar'),
        request: productSearchSimilarRequest,
        result: productSearchSimilarResult.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-search-combinations'),
        request: productSearchCombinationsRequest,
        result: productSearchCombinationsResult.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-search-complementary-category'),
        request: productSearchComplementaryCategoryRequest,
        result: productSearchComplementaryCategoryResult.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-mix-and-match'),
        request: productMixAndMatchRequest,
        result: productMixAndMatchResult.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-search-new'),
        request: productSearchNewRequest,
        result: productSearchNewResult.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-search-similar-by-image'),
        request: productSearchSimilarByImageRequest,
        result: productSearchSimilarByImageResult.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-search-combinations-by-image'),
        request: productSearchCombinationsByImageRequest,
        result: productSearchCombinationsByImageResult.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-comparison'),
        request: productComparisonRequest,
        result: productComparisonResult.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-detail'),
        request: z.object({ productId: z.string() }),
        result: product.optional(),
        error: featureError.optional()
    }),
    z.object({
        featureType: z.literal('product-multi-variant-add-to-cart'),
        request: z.object({ productIds: z.array(z.string()) }),
        result: z.array(product).optional(),
        error: featureError.optional()
    })
]);

export type Feature = TypeOf<typeof feature>;
export type FeatureType = Feature['featureType'];

export const assistantChatMessageContent = z.union([
    z.object({
        contentType: z.literal('markdown'),
        markdown: z.string()
    }),
    z.object({
        contentType: z.literal('feature'),
        startedBy: z.enum([
            'user-from-ui',
            'assistant-automated',
            'user-from-assistant-button'
        ]),
        status: z.enum(['loading', 'success', 'error']),
        applyFeature: z.boolean().optional(),
        feature
    }),
    z.object({
        contentType: z.literal('button'),
        featureType: z.enum([
            'product-search-similar',
            'product-search-combinations',
            'product-mix-and-match'
        ]),
        contextualProductIds: z.array(z.string().min(1))
    }),
    z.object({
        contentType: z.literal('apply-filters'),
        fieldFilters: z.array(
            fieldFilter.omit({ fieldType: true, color: true })
        ),
        categoryFilters: z.array(categoryFilter)
    }),
    z.object({
        contentType: z.literal('add-to-cart'),
        product: product,
        variantId: z.string()
    })
]);

export const assistantChatMessage = baseChatMessage.extend({
    role: z.literal('assistant'),
    content: assistantChatMessageContent,
    stop: z.boolean().optional()
});

export const chatMessage = z.union([userChatMessage, assistantChatMessage]);

export type ChatMessage = TypeOf<typeof chatMessage>;
export type UserChatMessage = TypeOf<typeof userChatMessage>;
export type AssistantChatMessage = TypeOf<typeof assistantChatMessage>;
export type AssistantChatMessageContent = TypeOf<
    typeof assistantChatMessageContent
>;
